import React, { useState } from 'react';
import InlineCellEditor from './InlineCellEditor';
import ColorPicker from './ColorPicker';
import { FONT_OPTIONS_INHERIT } from './fontOptions';

const btnStyle = {
  background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
  cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, padding: '2px 8px',
};

// Re-exported from here because this is where they used to live.
export { ColorPicker, PRESET_COLORS } from './ColorPicker';

export default function CustomTableEditor({ columns, rows, rowStyles, tableStyle, cellStyles, columnStyles, onChange }) {
  const cols = columns || [{ label: 'Column 1' }, { label: 'Column 2' }];
  const data = rows || [['', '']];
  const rStyles = rowStyles || [];
  const cStyles = cellStyles || [];
  const colStyles = columnStyles || [];
  const tStyle = tableStyle || {};

  const [activeRowMenu, setActiveRowMenu] = useState(null);
  const [activeColMenu, setActiveColMenu] = useState(null);

  const getColSpan = (ri, ci) => {
    const row = cStyles[ri];
    if (!row || !row[ci]) return 1;
    return row[ci].colSpan || 1;
  };

  const isMergedAway = (ri, ci) => {
    for (let c = 0; c < ci; c++) {
      const span = getColSpan(ri, c);
      if (c + span > ci) return true;
    }
    return false;
  };

  const mergeCells = (ri, ci) => {
    const currentSpan = getColSpan(ri, ci);
    if (ci + currentSpan >= cols.length) return;
    setCellStyle(ri, ci, 'colSpan', currentSpan + 1);
  };

  const unmergeCells = (ri, ci) => {
    const currentSpan = getColSpan(ri, ci);
    if (currentSpan <= 1) return;
    setCellStyle(ri, ci, 'colSpan', currentSpan > 2 ? currentSpan - 1 : null);
  };

  const fire = (nextCols, nextRows, nextRowStyles, nextTableStyle, nextCellStyles, nextColStyles) => {
    onChange(
      nextCols || cols,
      nextRows || data,
      nextRowStyles !== undefined ? nextRowStyles : rStyles,
      nextTableStyle !== undefined ? nextTableStyle : tStyle,
      nextCellStyles !== undefined ? nextCellStyles : cStyles,
      nextColStyles !== undefined ? nextColStyles : colStyles
    );
  };

  const updateCol = (ci, updates) => {
    fire(cols.map((c, i) => (i === ci ? { ...c, ...updates } : c)));
  };
  const addCol = () => {
    // Add empty cell styles column for each row
    const nextCellStyles = cStyles.map(row => [...(row || []), null]);
    fire([...cols, { label: `Column ${cols.length + 1}` }], data.map(r => [...r, '']), undefined, undefined, nextCellStyles);
  };
  const removeCol = (ci) => {
    if (cols.length <= 1) return;
    const nextCellStyles = cStyles.map(row => (row || []).filter((_, i) => i !== ci));
    fire(cols.filter((_, i) => i !== ci), data.map(r => r.filter((_, i) => i !== ci)), undefined, undefined, nextCellStyles);
  };
  const updateCell = (ri, ci, value) => {
    fire(null, data.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? value : c)) : r)));
  };
  const addRow = () => {
    fire(null, [...data, new Array(cols.length).fill('')], [...rStyles, null], undefined, [...cStyles, null]);
  };
  const removeRow = (ri) => {
    if (data.length <= 1) return;
    fire(null, data.filter((_, i) => i !== ri), rStyles.filter((_, i) => i !== ri), undefined, cStyles.filter((_, i) => i !== ri));
  };
  const insertRowBelow = (ri) => {
    const newRow = new Array(cols.length).fill('');
    const nextData = [...data]; nextData.splice(ri + 1, 0, newRow);
    const nextRStyles = [...rStyles]; nextRStyles.splice(ri + 1, 0, null);
    const nextCStyles = [...cStyles]; nextCStyles.splice(ri + 1, 0, null);
    fire(null, nextData, nextRStyles, undefined, nextCStyles);
  };

  const setRowStyle = (ri, key, value) => {
    const next = [...rStyles];
    while (next.length <= ri) next.push(null);
    next[ri] = { ...(next[ri] || {}), [key]: value };
    // Every key this entry can carry has to be listed, or setting only that
    // key leaves the entry "empty" and it is thrown away on the way out.
    const v = next[ri];
    if (!v.bg && !v.textColor && !v.font && !v.fontSize && !v.align && !v.height) next[ri] = null;
    fire(null, null, next);
  };

  // One write, not four. Each setRowStyle call rebuilds from the same `rStyles`
  // closure, so clearing field by field had every call start from the stale
  // array and only the last survive -- which left the row still styled.
  const clearRowStyle = (ri) => {
    const next = [...rStyles];
    while (next.length <= ri) next.push(null);
    next[ri] = null;
    fire(null, null, next);
  };

  // Formatting for a whole column, the counterpart of setRowStyle. Precedence
  // in the renderers is cell, then row, then column: the narrower statement
  // wins, and a column is the broadest of the three.
  const setColStyle = (ci, key, value) => {
    const next = [...colStyles];
    while (next.length <= ci) next.push(null);
    next[ci] = { ...(next[ci] || {}), [key]: value };
    const v = next[ci];
    // Drop an entry that no longer says anything, so an untouched table keeps
    // an empty array rather than a row of nulls.
    if (!v.bg && !v.textColor && !v.font && !v.fontSize && !v.bold && !v.align) next[ci] = null;
    fire(null, null, undefined, undefined, undefined, next);
  };

  const setCellStyle = (ri, ci, key, value) => {
    const next = [...cStyles];
    while (next.length <= ri) next.push(null);
    if (!next[ri]) next[ri] = [];
    const row = [...next[ri]];
    while (row.length <= ci) row.push(null);
    row[ci] = { ...(row[ci] || {}), [key]: value };
    // Clean up null values
    if (!row[ci].bg && !row[ci].textColor && !row[ci].fontSize && !row[ci].font && !row[ci].colSpan) row[ci] = null;
    next[ri] = row;
    // Clean up empty rows
    if (next[ri].every(c => c === null)) next[ri] = null;
    fire(null, null, undefined, undefined, next);
  };

  const getCellStyle = (ri, ci) => {
    const row = cStyles[ri];
    if (!row) return {};
    return row[ci] || {};
  };

  const setTableStyle = (key, value) => {
    fire(null, null, undefined, { ...tStyle, [key]: value });
  };

  const headerBg = tStyle.headerBg || '#f3f4f6';
  const headerTextColor = tStyle.headerTextColor || '#000000';
  const bStyle = tStyle.borderStyle || 'full';
  const borderColor = bStyle === 'none' ? 'transparent' : (tStyle.borderColor || '#e5e7eb');
  const verticalBorder = bStyle === 'full' ? `1px solid ${borderColor}` : 'none';

  return (
    <div>
      {/* Table Style Controls */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
        padding: '8px 10px', marginBottom: 6, background: 'var(--bg-input)',
        borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 11
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Header BG:</span>
          <ColorPicker value={headerBg} onChange={v => setTableStyle('headerBg', v || '#f3f4f6')} label="Header background" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Header Text:</span>
          <ColorPicker value={headerTextColor} onChange={v => setTableStyle('headerTextColor', v || '#000000')} label="Header text color" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Borders:</span>
          <ColorPicker value={borderColor} onChange={v => setTableStyle('borderColor', v || '#e5e7eb')} label="Border color" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Font:</span>
          <select value={tStyle.font || ''} onChange={e => setTableStyle('font', e.target.value || null)}
            style={{ padding: '2px 6px', fontSize: 11, borderRadius: 'var(--radius)',
                     border: '1px solid var(--border)', background: 'var(--bg-card)',
                     color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit' }}>
            {FONT_OPTIONS_INHERIT.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Style:</span>
          {['full', 'horizontal', 'none'].map(s => (
            <button key={s} onClick={() => setTableStyle('borderStyle', s)} style={{
              padding: '2px 8px', fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
              background: (tStyle.borderStyle || 'full') === s ? 'var(--primary)' : 'var(--bg-card)',
              color: (tStyle.borderStyle || 'full') === s ? '#fff' : 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
            }}>{s}</button>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
          title="Uncheck to output the table with no header row. The column names stay here for editing.">
          <input type="checkbox" checked={tStyle.showHeader !== false}
            onChange={e => setTableStyle('showHeader', e.target.checked)} />
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Header row</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={tStyle.stripedRows || false}
            onChange={e => setTableStyle('stripedRows', e.target.checked)} />
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Striped</span>
        </label>
        {tStyle.stripedRows && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Stripe:</span>
            <ColorPicker value={tStyle.stripedColor || '#f9fafb'} onChange={v => setTableStyle('stripedColor', v || '#f9fafb')} label="Striped row color" />
          </div>
        )}
      </div>

      {tStyle.showHeader === false && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
          The header row is off, so the generated table starts at the first data row.
          Column names stay below because they are still how you size, align and merge
          columns — they just are not output.
        </div>
      )}

      {/* Table Editor */}
      <div style={{ border: `1px solid ${borderColor}`, borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: headerBg }}>
              <th style={{ width: 28, padding: 4, borderBottom: `1px solid ${borderColor}` }} />
              {cols.map((c, ci) => (
                <th key={ci} style={{ padding: 4, borderBottom: `1px solid ${borderColor}`, borderRight: ci < cols.length - 1 ? verticalBorder : 'none', width: c.width ? `${c.width}%` : undefined }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      value={c.label}
                      onChange={e => updateCol(ci, { label: e.target.value })}
                      style={{
                        flex: 1, border: 'none', background: 'transparent', fontWeight: 600,
                        fontSize: 12, padding: '2px 4px', color: headerTextColor, outline: 'none', minWidth: 0
                      }}
                    />
                    <input
                      type="number"
                      value={c.width || ''}
                      placeholder="Auto"
                      onChange={e => updateCol(ci, { width: e.target.value ? Number(e.target.value) : null })}
                      title="Column width %"
                      style={{
                        width: 40, border: '1px solid var(--border)', background: 'var(--bg-input)',
                        borderRadius: 3, fontSize: 9, padding: '1px 3px', color: 'var(--text)',
                        textAlign: 'center'
                      }}
                    />
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setActiveColMenu(activeColMenu === ci ? null : ci)}
                        style={{ ...btnStyle, border: 'none', padding: '0 4px', fontSize: 12,
                                 color: (colStyles[ci] && (colStyles[ci].bg || colStyles[ci].textColor || colStyles[ci].font || colStyles[ci].fontSize || colStyles[ci].bold)) ? 'var(--primary)' : 'var(--text-secondary)' }}
                        title="Column style"
                      >🎨</button>
                      {activeColMenu === ci && (
                        <div style={{
                          position: 'absolute', right: 0, top: 20, zIndex: 100,
                          background: 'var(--bg-card)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius)', padding: 8, boxShadow: 'var(--shadow-lg)',
                          minWidth: 150, fontSize: 11, textAlign: 'left', fontWeight: 400,
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>
                            Column {ci + 1} Style
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={{ color: 'var(--text-secondary)' }}>BG:</span>
                            <ColorPicker value={(colStyles[ci] || {}).bg || null}
                              onChange={v => setColStyle(ci, 'bg', v)} label="Column background" />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Text:</span>
                            <ColorPicker value={(colStyles[ci] || {}).textColor || null}
                              onChange={v => setColStyle(ci, 'textColor', v)} label="Column text colour" />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Font:</span>
                            <select value={(colStyles[ci] || {}).font || ''}
                              onChange={e => setColStyle(ci, 'font', e.target.value || null)}
                              style={{ flex: 1, padding: '2px 4px', fontSize: 10, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)' }}>
                            <option value="">Default</option>
                            <option value="Helvetica">Helvetica</option>
                            <option value="Times-Roman">Times Roman</option>
                            <option value="Courier">Courier</option>
                            <option value="Calibri">Calibri</option>
                            <option value="Arial">Arial</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Size:</span>
                            <select value={(colStyles[ci] || {}).fontSize || ''}
                              onChange={e => setColStyle(ci, 'fontSize', e.target.value ? Number(e.target.value) : null)}
                              style={{ flex: 1, padding: '2px 4px', fontSize: 10, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)' }}>
                              <option value="">Default</option>
                              {[7,8,9,10,11,12,13,14,16,18,20,24,28,32].map(s => <option key={s} value={s}>{s}pt</option>)}
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Align:</span>
                            <select value={(colStyles[ci] || {}).align || ''}
                              onChange={e => setColStyle(ci, 'align', e.target.value || null)}
                              style={{ flex: 1, padding: '2px 4px', fontSize: 10, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)' }}>
                              <option value="">Default</option>
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                              <option value="justify">Justify</option>
                            </select>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!(colStyles[ci] || {}).bold}
                              onChange={e => setColStyle(ci, 'bold', e.target.checked || null)} />
                            <span style={{ color: 'var(--text-secondary)' }}>Bold</span>
                          </label>
                          <button onClick={() => {
                              const next = [...colStyles];
                              while (next.length <= ci) next.push(null);
                              next[ci] = null;
                              fire(null, null, undefined, undefined, undefined, next);
                              setActiveColMenu(null);
                            }}
                            style={{ ...btnStyle, width: '100%', textAlign: 'center', fontSize: 10, marginTop: 2 }}>
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                    {cols.length > 1 && (
                      <button onClick={() => removeCol(ci)} style={{ ...btnStyle, color: '#ef4444', border: 'none', padding: '0 4px' }} title="Remove column">x</button>
                    )}
                  </div>
                </th>
              ))}
              <th style={{ width: 30, padding: 4, borderBottom: `1px solid ${borderColor}` }}>
                <button onClick={addCol} style={{ ...btnStyle, fontSize: 14, padding: '0 6px' }} title="Add column">+</button>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, ri) => {
              const rs = rStyles[ri] || {};
              const isStriped = tStyle.stripedRows && ri % 2 === 1;
              const rowBg = rs.bg || (isStriped ? (tStyle.stripedColor || '#f9fafb') : undefined);
              return (
                <tr key={ri} style={{ background: rowBg }}>
                  <td style={{ width: 28, padding: 2, textAlign: 'center', borderBottom: ri < data.length - 1 ? `1px solid ${borderColor}` : 'none', position: 'relative' }}>
                    <button
                      onClick={() => setActiveRowMenu(activeRowMenu === ri ? null : ri)}
                      style={{ ...btnStyle, border: 'none', padding: '0 4px', fontSize: 14, color: rs.bg || rs.textColor ? 'var(--primary)' : 'var(--text-secondary)' }}
                      title="Row style"
                    >🎨</button>
                    {activeRowMenu === ri && (
                      <div style={{
                        position: 'absolute', left: 28, top: 0, zIndex: 100,
                        background: 'var(--bg-card)', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)', padding: 8, boxShadow: 'var(--shadow-lg)',
                        minWidth: 150, fontSize: 11
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>Row {ri + 1} Style</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>BG:</span>
                          <ColorPicker value={rs.bg || null} onChange={v => setRowStyle(ri, 'bg', v)} label="Row background" />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Text:</span>
                          <ColorPicker value={rs.textColor || null} onChange={v => setRowStyle(ri, 'textColor', v)} label="Row text color" />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Font:</span>
                          <select value={rs.font || ''} onChange={e => setRowStyle(ri, 'font', e.target.value || null)}
                            style={{ flex: 1, padding: '2px 4px', fontSize: 10, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)' }}>
                            <option value="">Default</option>
                            <option value="Helvetica">Helvetica</option>
                            <option value="Times-Roman">Times Roman</option>
                            <option value="Courier">Courier</option>
                            <option value="Calibri">Calibri</option>
                            <option value="Arial">Arial</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Size:</span>
                          <select value={rs.fontSize || ''} onChange={e => setRowStyle(ri, 'fontSize', e.target.value ? Number(e.target.value) : null)}
                            style={{ flex: 1, padding: '2px 4px', fontSize: 10, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)' }}>
                            <option value="">Default</option>
                            {[7,8,9,10,11,12,13,14,16,18,20,24,28,32].map(s => <option key={s} value={s}>{s}pt</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Align:</span>
                          <select value={rs.align || ''} onChange={e => setRowStyle(ri, 'align', e.target.value || null)}
                            style={{ flex: 1, padding: '2px 4px', fontSize: 10, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)' }}>
                            <option value="">Default</option>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                            <option value="justify">Justify</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Height:</span>
                          <input type="number" min="0" step="1" value={rs.height || ''} placeholder="auto"
                            aria-label={`Row ${ri + 1} height in points`}
                            onChange={e => setRowStyle(ri, 'height', e.target.value ? Number(e.target.value) : null)}
                            style={{ flex: 1, minWidth: 0, padding: '2px 4px', fontSize: 10, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)' }} />
                          <span style={{ color: 'var(--text-muted)' }}>pt</span>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6 }}>
                          Height is a minimum &mdash; a row needing more room still takes it.
                        </div>
                        <button onClick={() => { clearRowStyle(ri); setActiveRowMenu(null); }}
                          style={{ ...btnStyle, width: '100%', textAlign: 'center', fontSize: 10, marginTop: 2 }}>
                          Clear
                        </button>
                      </div>
                    )}
                  </td>
                  {row.map((cell, ci) => {
                    if (isMergedAway(ri, ci)) return null;
                    const cs = getCellStyle(ri, ci);
                    const colS = colStyles[ci] || {};
                    const span = getColSpan(ri, ci);
                    return (
                      <td key={ci} colSpan={span > 1 ? span : undefined} style={{ padding: 0, borderBottom: ri < data.length - 1 ? `1px solid ${borderColor}` : 'none', borderRight: ci + span - 1 < cols.length - 1 ? `1px solid ${borderColor}` : 'none', verticalAlign: 'top', position: 'relative' }}>
                        <InlineCellEditor
                          value={cell}
                          onChange={v => updateCell(ri, ci, v)}
                          textColor={rs.textColor || colS.textColor || undefined}
                          cellBg={cs.bg || (!rowBg ? colS.bg : undefined) || undefined}
                          cellTextColor={cs.textColor || undefined}
                          cellFontSize={cs.fontSize || undefined}
                          cellFont={cs.font || undefined}
                          onCellStyleChange={(key, value) => setCellStyle(ri, ci, key, value)}
                        />
                        <div style={{ position: 'absolute', top: 1, right: 1, display: 'flex', gap: 1 }}>
                          {ci + span < cols.length && (
                            <button onClick={() => mergeCells(ri, ci)} title="Merge right" style={{ ...btnStyle, padding: '0 3px', fontSize: 9, lineHeight: 1, border: 'none', opacity: 0.5 }}>→⊕</button>
                          )}
                          {span > 1 && (
                            <button onClick={() => unmergeCells(ri, ci)} title="Unmerge" style={{ ...btnStyle, padding: '0 3px', fontSize: 9, lineHeight: 1, border: 'none', color: '#ef4444', opacity: 0.5 }}>⊖</button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td style={{ width: 50, padding: 2, textAlign: 'center', borderBottom: ri < data.length - 1 ? `1px solid ${borderColor}` : 'none', whiteSpace: 'nowrap' }}>
                    <button onClick={() => insertRowBelow(ri)} style={{ ...btnStyle, color: '#22c55e', border: 'none', padding: '0 4px' }} title="Add row below">+</button>
                    {data.length > 1 && (
                      <button onClick={() => removeRow(ri)} style={{ ...btnStyle, color: '#ef4444', border: 'none', padding: '0 4px' }} title="Remove row">x</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ padding: '4px 8px', borderTop: `1px solid ${borderColor}`, background: 'var(--bg-input)' }}>
          <button onClick={addRow} style={{ ...btnStyle, width: '100%', padding: '4px 0' }}>+ Add Row</button>
        </div>
      </div>
    </div>
  );
}
